import React from 'react';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import SchoolIcon from '@mui/icons-material/School';
import TranslateIcon from '@mui/icons-material/Translate';
import GradeIcon from '@mui/icons-material/Grade';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BuildIcon from '@mui/icons-material/Build';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BadgeIcon from '@mui/icons-material/Badge';
import CategoryIcon from '@mui/icons-material/Category';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, MainSection } from '../common/styles';
import { StyledCard, StyledIconButton } from '../LandingPage/styles';
import { COLORS } from '../../constants/radar';
import { useAbility } from '../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';

const configCards = [
  {
    id: 'education-degree',
    title: 'Education Degree',
    description: 'Configure education degree options for CV',
    icon: <SchoolIcon />,
    path: '/cv-configuration/education-degree',
    customIconStyle: {
      backgroundColor: '#9C27B0',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_EDUCATION_DEGREE
    }
  },
  {
    id: 'languages',
    title: 'Languages',
    description: 'Configure language options for CV',
    icon: <TranslateIcon />,
    path: '/cv-configuration/languages',
    customIconStyle: {
      backgroundColor: '#FF5722',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_LANGUAGE
    }
  },
  {
    id: 'language-level',
    title: 'Language Level',
    description: 'Configure language proficiency levels for CV',
    icon: <GradeIcon />,
    path: '/cv-configuration/language-level',
    customIconStyle: {
      backgroundColor: '#4CAF50',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_LANGUAGE_LEVEL
    }
  },
  {
    id: 'certification-issuer',
    title: 'Certification Issuer',
    description: 'Configure certification issuers, expiration settings, and certification catalogs',
    icon: <WorkspacePremiumIcon />,
    path: '/cv-configuration/certification-issuer',
    customIconStyle: {
      backgroundColor: '#2196F3',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_CERTIFICATION_ISSUER
    }
  },
  {
    id: 'certification-category',
    title: 'Certification Category',
    description: 'Configure certification category options for CV',
    icon: <CategoryIcon />,
    path: '/cv-configuration/certification-category',
    customIconStyle: {
      backgroundColor: '#009688',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_CERTIFICATION_ISSUER
    }
  },
  {
    id: 'skills-reconciliation',
    title: 'Skills Reconciliation',
    description: 'Manage and reconcile skills across CV profiles',
    icon: <BuildIcon />,
    path: '/cv-configuration/skills-reconciliation',
    customIconStyle: {
      backgroundColor: '#FFC107',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_SKILLS_RECONCILIATION
    }
  },
  {
    id: 'certification-issuer-reconciliation',
    title: 'Certification Issuer Reconciliation',
    description: 'Manage and reconcile certification issuers across CV profiles',
    icon: <WorkspacePremiumIcon />,
    path: '/cv-configuration/certification-issuer-reconciliation',
    customIconStyle: {
      backgroundColor: '#E91E63',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_CERTIFICATION_ISSUER_RECONCILIATION
    }
  },
  {
    id: 'certification-name-reconciliation',
    title: 'Certification Name Reconciliation',
    description: 'Reconcile custom certification names to catalog entries per issuer',
    icon: <MergeTypeIcon />,
    path: '/cv-configuration/certification-name-reconciliation',
    customIconStyle: {
      backgroundColor: '#FF9800',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_CERTIFICATION_ISSUER
    }
  },
  {
    id: 'custom-projects-reconciliation',
    title: 'Custom Projects Reconciliation',
    description: 'Manage and reconcile custom projects across CV profiles',
    icon: <AccountTreeIcon />,
    path: '/cv-configuration/custom-projects-reconciliation',
    customIconStyle: {
      backgroundColor: '#00BCD4',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_CUSTOM_PROJECTS_RECONCILIATION
    }
  },
  {
    id: 'custom-classifications-reconciliation',
    title: 'Custom Classifications Reconciliation',
    description: 'Manage and reconcile custom job titles across CV profiles',
    icon: <BadgeIcon />,
    path: '/cv-configuration/custom-classifications-reconciliation',
    customIconStyle: {
      backgroundColor: '#673AB7',
    },
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_CUSTOM_CLASSIFICATIONS_RECONCILIATION
    }
  }
];

const CVConfiguration = () => {
  const navigate = useNavigate();
  const ability = useAbility();

  // Filter cards based on user permissions
  const visibleCards = configCards.filter(card => {
    if (!card.permission) {
      return true;
    }
    return ability.can(card.permission.action, card.permission.subject);
  });

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Dashboard
          </BackButton>
          <Title>CV Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection>
          <Typography variant="body1" paragraph>
            Select a configuration option to manage CV settings
          </Typography>

          <Grid container spacing={3}>
            {visibleCards.map((card) => (
              <Grid item xs={12} md={6} key={card.id}>
                <StyledCard onClick={() => navigate(card.path)}>
                  <StyledIconButton
                    sx={{
                      ...card.customIconStyle,
                      '&:hover': {
                        ...card.customIconStyle
                      }
                    }}
                  >
                    {card.icon}
                  </StyledIconButton>
                  <Typography variant="h6" align="center">{card.title}</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    {card.description}
                  </Typography>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </MainSection>
      </Content>
    </Container>
  );
};

export default CVConfiguration; 